/**
 * PdfCommentsPopover - Modal popover showing all document comments, opened by button
 */

import React, { useEffect, useRef } from 'react';
import { X, MessageSquare } from 'lucide-react';
import type { PdfAnnotation } from '../../types';
import { pdfAnnotationHasUnreadForUser } from '../../utils/pdfAnnotationService';
import './PdfCommentsPopover.css';

interface PdfCommentsPopoverProps {
  annotations: PdfAnnotation[];
  currentPageNumber: number;
  currentUserId?: string;
  onSelectComment: (annotationId: string, pageNumber: number) => void;
  onClose: () => void;
}

function groupByPage(annotations: PdfAnnotation[]): Map<number, PdfAnnotation[]> {
  const byPage = new Map<number, PdfAnnotation[]>();
  for (const ann of annotations) {
    const page = ann.pageNumber;
    if (!byPage.has(page)) byPage.set(page, []);
    byPage.get(page)!.push(ann);
  }
  Array.from(byPage.values()).forEach(arr => {
    arr.sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime());
  });
  return byPage;
}

export function PdfCommentsPopover({
  annotations,
  currentPageNumber,
  currentUserId,
  onSelectComment,
  onClose,
}: PdfCommentsPopoverProps) {
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const byPage = groupByPage(annotations);
  const pages = Array.from(byPage.keys()).sort((a, b) => a - b);

  const isUnread = (ann: PdfAnnotation) =>
    !!currentUserId && pdfAnnotationHasUnreadForUser(ann, currentUserId);

  return (
    <div className="pdf-comments-popover-overlay" onClick={e => { e.stopPropagation(); onClose(); }}>
      <div className="pdf-comments-popover" onClick={e => e.stopPropagation()}>
        <div className="pdf-comments-popover-header">
          <MessageSquare size={18} />
          <h2>Comments</h2>
          <button
            type="button"
            className="pdf-comments-popover-close"
            onClick={onClose}
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>
        <div ref={listRef} className="pdf-comments-popover-list">
          {annotations.length === 0 ? (
            <p className="pdf-comments-popover-empty">No comments yet</p>
          ) : (
            pages.map(page => (
              <div key={page} className="pdf-comments-popover-page">
                <div className="pdf-comments-popover-page-title">
                  Page {page}
                </div>
                {byPage.get(page)!.map(ann => (
                  <button
                    key={ann.id}
                    type="button"
                    className={`pdf-comments-popover-item ${currentPageNumber === page ? 'on-current-page' : ''} ${isUnread(ann) ? 'unread' : ''}`}
                    onClick={() => {
                      if (ann.id) {
                        onSelectComment(ann.id, page);
                        onClose();
                      }
                    }}
                  >
                    {isUnread(ann) && <span className="pdf-comments-popover-unread-dot" aria-label="Unread" />}
                    <span className="pdf-comments-popover-author">{ann.userName}</span>
                    <span className="pdf-comments-popover-preview">
                      {ann.content.slice(0, 80)}{ann.content.length > 80 ? '…' : ''}
                    </span>
                    {ann.replies && ann.replies.length > 0 && (
                      <span className="pdf-comments-popover-reply-count">
                        {ann.replies.length} {ann.replies.length === 1 ? 'reply' : 'replies'}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
